import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { selectCurrentUser } from '../store/slices/authSlice';
import { useNavigate } from 'react-router-dom';

function NotificationsPanel() {
  const currentUser = useSelector(selectCurrentUser);
  const navigate = useNavigate();
  const [showPanel, setShowPanel] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

  useEffect(() => {
    if (currentUser?.username) {
      loadNotifications();
      const interval = setInterval(loadNotifications, 30000);
      return () => clearInterval(interval);
    }
  }, [currentUser]);

  const loadNotifications = async () => {
    if (!currentUser?.username) return;
    
    try {
      const response = await fetch(API_BASE_URL + '/notifications?username=' + currentUser.username + '&limit=20');
      if (response.ok) {
        const data = await response.json();
        setNotifications(data.notifications || []);
        setUnreadCount(data.unreadCount || 0);
      }
    } catch (error) {
      console.error('Error loading notifications:', error);
    }
  };

  const handleMarkAsRead = async (id) => {
    try {
      const response = await fetch(API_BASE_URL + '/notifications/' + id + '/read', {
        method: 'PUT'
      });
      if (response.ok) {
        loadNotifications();
      }
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      const response = await fetch(API_BASE_URL + '/notifications/read-all', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: currentUser.username })
      });
      if (response.ok) {
        loadNotifications();
      }
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  const handleClearAll = async () => {
    if (!confirm('Xóa tất cả thông báo?')) return;
    
    try {
      const response = await fetch(API_BASE_URL + '/notifications?username=' + currentUser.username, {
        method: 'DELETE'
      });
      if (response.ok) {
        setNotifications([]);
        setUnreadCount(0);
      }
    } catch (error) {
      console.error('Error clearing notifications:', error);
    }
  };

  const getNotificationIcon = (type) => {
    const icons = {
      like: 'favorite',
      comment: 'comment',
      follow: 'person_add',
      pin_approved: 'check_circle',
      pin_rejected: 'cancel',
      collection_shared: 'share'
    };
    return icons[type] || 'notifications';
  };

  const getNotificationColor = (type) => {
    const colors = {
      like: 'text-red-500',
      comment: 'text-blue-500',
      follow: 'text-purple-500',
      pin_approved: 'text-green-500',
      pin_rejected: 'text-red-500',
      collection_shared: 'text-orange-500'
    };
    return colors[type] || 'text-gray-500';
  };

  const formatTimeAgo = (date) => {
    const seconds = Math.floor((new Date() - new Date(date)) / 1000);
    
    if (seconds < 60) return 'Vừa xong';
    if (seconds < 3600) return Math.floor(seconds / 60) + ' phút trước';
    if (seconds < 86400) return Math.floor(seconds / 3600) + ' giờ trước';
    if (seconds < 604800) return Math.floor(seconds / 86400) + ' ngày trước';
    return new Date(date).toLocaleDateString('vi-VN');
  };

  return (
    <div className="relative">
      <button
        onClick={() => setShowPanel(!showPanel)}
        className="relative w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-all hover:scale-110"
      >
        <span className="material-symbols-rounded text-gray-700 dark:text-gray-300">
          notifications
        </span>
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 bg-red-500 text-white text-xs rounded-full min-w-[20px] h-5 flex items-center justify-center px-1 font-bold shadow-lg animate-pulse">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {showPanel && (
        <>
          <div 
            className="fixed inset-0 z-40 bg-black/20" 
            onClick={() => setShowPanel(false)}
          ></div>
          
          <div className="fixed sm:absolute left-0 right-0 sm:left-auto top-0 sm:top-auto bottom-0 sm:bottom-auto sm:right-0 sm:mt-2 sm:w-96 sm:max-w-md bg-white dark:bg-gray-800 sm:rounded-2xl shadow-2xl z-50 animate-scale-in sm:max-h-[70vh] flex flex-col">
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h3 className="font-bold text-lg text-gray-900 dark:text-white">Thông báo</h3>
              <div className="flex items-center gap-2">
                {unreadCount > 0 && (
                  <button
                    onClick={handleMarkAllAsRead}
                    className="text-xs text-blue-600 dark:text-blue-400 hover:underline whitespace-nowrap"
                  >
                    Đánh dấu đã đọc
                  </button>
                )}
                {notifications.length > 0 && (
                  <button
                    onClick={handleClearAll}
                    className="text-xs text-red-600 dark:text-red-400 hover:underline whitespace-nowrap"
                  >
                    Xóa tất cả
                  </button>
                )}
                <button
                  onClick={() => setShowPanel(false)}
                  className="sm:hidden w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ml-2"
                >
                  <span className="material-symbols-rounded text-gray-600 dark:text-gray-400">close</span>
                </button>
              </div>
            </div>
            
            <div className="flex-1 overflow-y-auto">
              {isLoading ? (
                <div className="p-8 text-center">
                  <div className="w-8 h-8 border-4 border-gray-200 border-t-blue-600 rounded-full animate-spin mx-auto"></div>
                </div>
              ) : notifications.length === 0 ? (
                <div className="p-8 text-center">
                  <span className="material-symbols-rounded text-6xl text-gray-300 dark:text-gray-600 mb-4 block">
                    notifications_off
                  </span>
                  <p className="text-gray-500 dark:text-gray-400">Không có thông báo mới</p>
                </div>
              ) : (
                <div>
                  {notifications.map((notification) => (
                    <div
                      key={notification._id}
                      className={'p-4 border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-all ' + (!notification.isRead ? 'bg-blue-50 dark:bg-blue-900/20' : '')}
                      onClick={() => {
                        handleMarkAsRead(notification._id);
                        if (notification.targetType === 'pin' && notification.targetId) {
                          setShowPanel(false);
                        }
                      }}
                    >
                      <div className="flex items-start gap-3">
                        <div className={'w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ' + (notification.isRead ? 'bg-gray-100 dark:bg-gray-700' : 'bg-blue-100 dark:bg-blue-900/30')}>
                          <span className={'material-symbols-rounded text-[20px] ' + getNotificationColor(notification.type)}>
                            {getNotificationIcon(notification.type)}
                          </span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-gray-900 dark:text-white leading-relaxed break-words">
                            {notification.message}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                            {formatTimeAgo(notification.createdAt)}
                          </p>
                        </div>
                        {!notification.isRead && (
                          <div className="w-2 h-2 bg-blue-600 rounded-full flex-shrink-0 mt-2"></div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default NotificationsPanel;
